import { useEffect, useState } from "react";
import { MdClear } from "react-icons/md";
import { CiImageOff } from "react-icons/ci";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import useCompanies from "../useCompany"; // your CRUD hook

const EditCompanyDrawer = ({ isOpen, onClose, companyData, handleSnackbar }) => {
  const [formData, setFormData] = useState({
    logo: companyData?.logo || "",
    name: companyData?.name || "",
    email: companyData?.email || "",
    website: companyData?.website || "",
    linkedin: companyData?.linkedin || "",
    about: companyData?.about || "",
    startupStage: companyData?.startupStage?._id || "",
    industry: companyData?.industry?._id || "",
    haveRaisedCapital: companyData?.haveRaisedCapital ?? false,
  });

  const { fetchStartupStage, fetchIndustry, updateCompany,handleFileUpload } = useCompanies();
  const [startupStageOptions, setStartupStageOptions] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      const stages = await fetchStartupStage();
      const industries = await fetchIndustry();
      setStartupStageOptions(
        stages?.map((stage) => ({ label: stage.name, value: stage._id }))
      );
      setIndustryOptions(
        industries?.map((industry) => ({ label: industry.name, value: industry._id }))
      );
    };
    loadOptions();
  }, []);

  const validateForm = () => {
    return (
      formData.name.trim().length >= 3 &&
      (!formData.email || /\S+@\S+\.\S+/.test(formData.email)) &&
      (!formData.website || formData.website.startsWith("http")) &&
      formData.startupStage &&
      formData.industry
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
        const url=await handleFileUpload(file)
        setFormData((prev) => ({ ...prev, logo: url, }));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await updateCompany(companyData._id, formData);
      if (response) {
        handleSnackbar("Company details updated successfully!");
        onClose();
      } else {
        handleSnackbar("Failed to update company. Please try again.", "error");
      }
    } catch (error) {
      console.error("Error updating company:", error);
      handleSnackbar(
        `An unexpected error occurred: ${error?.message ?? error}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Background Overlay */}
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
            className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-50 flex flex-col rounded-l-2xl"
          >
            {/* Header */}
            <div className="flex justify-between items-center bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500 p-4 rounded-tl-2xl">
              <h2 className="text-2xl font-bold text-white">Edit Company</h2>
              <button onClick={onClose} className="text-xl text-white">
                <MdClear />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Logo Upload */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Company Logo
                </label>
                <div className="flex items-center gap-3">
                  {formData.logo ? (
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-md border">
                      <img
                        src={formData.logo}
                        alt="Company Logo"
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-md border text-gray-400">
                      <CiImageOff size={20} />
                    </div>
                  )}

                  <label className="cursor-pointer text-blue-600 hover:underline">
                    Change
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </label>
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  Website
                </label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  LinkedIn
                </label>
                <input
                  type="text"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleChange}
                  placeholder="LinkedIn profile URL"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* About */}
              <div>
                <label className="block text-gray-700 font-semibold mb-1">
                  About
                </label>
                <textarea
                  name="about"
                  value={formData.about}
                  onChange={handleChange}
                  placeholder="Brief company description"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows={3}
                />
              </div>

              {/* Startup Stage & Industry */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-1">
                    Startup Stage
                  </label>
                  <Select
                    isMulti={false}
                    options={startupStageOptions}
                    value={
                      startupStageOptions.find(
                        (option) => option.value === formData.startupStage
                      ) || null
                    }
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        startupStage: selected ? selected.value : "",
                      }))
                    }
                    placeholder="Select Startup Stage"
                    classNamePrefix="react-select"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">
                    Industry
                  </label>
                  <Select
                    isMulti={false}
                    options={industryOptions}
                    value={
                      industryOptions.find(
                        (option) => option.value === formData.industry
                      ) || null
                    }
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        industry: selected ? selected.value : "",
                      }))
                    }
                    placeholder="Select Industry"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>

              {/* Raised Capital */}
              <div className="flex items-center mt-2">
                <input
                  id="haveRaisedCapital"
                  name="haveRaisedCapital"
                  type="checkbox"
                  checked={formData.haveRaisedCapital}
                  onChange={handleChange}
                  className="mr-2"
                />
                <label
                  htmlFor="haveRaisedCapital"
                  className="text-gray-700 font-semibold"
                >
                  Have raised capital
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end space-x-4 p-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!validateForm() || isSubmitting}
                className={`px-4 py-2 rounded-md ${
                  validateForm()
                    ? "bg-green-500 text-white hover:bg-green-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditCompanyDrawer;
